import { WmPagination } from "@watermarkinsights/ripple-react";
import { useEffect, useContext, useReducer } from "react";
import { sendRequest } from "@/common/http";
import { useIntl } from "react-intl";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import translate from "@/i18n/translate";
import { formatDateWithMonthAbbr } from "@/features/common/helper";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import { FolderIcon, CheckCircleFilled } from "@/features/common/Icons";
import {
  pageSize,
  isTaskInCurrentPage
} from "@/features/common/tabComponent/InsightsTabComponent";

interface CompletedTasksState {
  completedTaskCount: number;
  completedTasks: CompletedTask[] | null;
  loadedCompletedTasks: boolean;
  currentPage: number;
}

interface CompletedTasksAction {
  type: string;
  completedTasks: CompletedTask[] | null;
  currentPage: number;
}

const defaultAction: CompletedTasksAction = {
  type: "",
  completedTasks: [],
  currentPage: 1
};

interface CompletedTask {
  title: string;
  completedDate: string;
  product: string;
  type: string;
  state: string;
}

const reducer = (state: CompletedTasksState, action: CompletedTasksAction) => {
  const currentCompletedTaskCount = state.completedTaskCount;
  switch (action.type) {
    case "setCompletedTasks":
      const completedTaskCount = action.completedTasks
        ? currentCompletedTaskCount + action.completedTasks.length
        : currentCompletedTaskCount;
      return {
        ...state,
        completedTasks: action.completedTasks,
        completedTaskCount: completedTaskCount,
        loadedCompletedTasks: true
      };
    case "setPage":
      return { ...state, currentPage: action.currentPage };
    default:
      return state;
  }
};

const initialState: CompletedTasksState = {
  completedTaskCount: 0,
  completedTasks: null,
  loadedCompletedTasks: false,
  currentPage: 1
};

export function CompletedTasks() {
  const { institution, sysUser } = useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    if (institution && institution.salesforceId) {
      const applyResult = (result: CompletedTask[]) => {
        dispatch({
          ...defaultAction,
          type: "setCompletedTasks",
          completedTasks: result
        });
      };

      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setCompletedTasks",
          completedTasks: []
        });
      };

      sendRequest(
        {
          uri: `/home/completed-tasks`,
          camelize: true
        },
        applyResult,
        onError
      );
    }
  }, [institution]);

  function showEmptyState() {
    return state.completedTasks && state.completedTasks?.length === 0;
  }

  function showCompletedTasks() {
    return (
      state.loadedCompletedTasks &&
      state.completedTasks &&
      state.completedTasks.length > 0
    );
  }
  const setPage = (event: any) => {
    dispatch({
      ...defaultAction,
      type: "setPage",
      currentPage: event.target.value
    });
  };

  return (
    <div className="tab-container">
      {showEmptyState() && <CompletedEmptyState />}

      {!state.loadedCompletedTasks && (
        <WidgetLoader
          loaderContainerClasses="loader-container"
          loaderClasses="widget-loader"
        />
      )}

      {showCompletedTasks() && (
        <>
          {state.completedTasks
            ?.filter((_, index: number) =>
              isTaskInCurrentPage(index, state.currentPage)
            )
            .map((task: CompletedTask, index: number) => (
              <RenderCompletedTask key={`${task.title}-${index}`} task={task} />
            ))}

          {state.completedTaskCount > pageSize && (
            <WmPagination
              currentPage={state.currentPage}
              itemsPerPage={pageSize}
              totalItems={state.completedTaskCount}
              onWmPaginationPageClicked={setPage}
            ></WmPagination>
          )}
        </>
      )}
    </div>
  );
}

function CompletedEmptyState() {
  return (
    <>
      <div className="tab-content-title">
        {translate("app.home.tasksCompleted", { count: 0 })}
      </div>
      <div className="tab-content-desc">
        {translate("app.home.completedEmptyState")}
      </div>
    </>
  );
}

const formatTaskDetails = (product: string, title: string, state: string) => {
  switch (product.toLowerCase()) {
    case "faculty success":
      return [
        useIntl().formatMessage(
          { id: "app.home.fsTaskTitle" },
          { title: title }
        ),
        useIntl().formatMessage({ id: "app.home.fsTaskDesc" }, { title: title })
      ];
    default:
      return ["", ""];
  }
};

function RenderCompletedTask({ task }: { task: CompletedTask }) {
  const [taskTitle, taskDescription] = formatTaskDetails(
    task.product,
    task.title,
    task.state
  );

  const completedDate = useIntl().formatMessage(
    { id: "app.home.taskCompletedDate" },
    { date: formatDateWithMonthAbbr(task.completedDate) }
  );
  return (
    <div className="task-container completed-task">
      <div className="flex-row create-reporting-year-link task-content">
        <div className="flex-row create-reporting-year-link title-container">
          <span>
            <FolderIcon color={"#6B6B6B"} />
          </span>
          <span className="title">{taskTitle}</span>
        </div>

        <div className="flex-row completed-date">
          <CheckCircleFilled />
          {task.completedDate && (
            <div className="task-due-date read-more">{completedDate}</div>
          )}
        </div>
      </div>
      <div className="task-content flex-row">
        <div className="task-description">
          {taskDescription} {task.product}.
        </div>
      </div>
    </div>
  );
}
